import { ConfigurationContext, getEmailTokenPath } from '@news-mono/web-common'
import { useContext } from 'react'
import { useMutation, useQuery } from 'react-query'
import { EmailPreferenceSection } from '../components/PreferenceSection'

const PreferenceGroup = [
    'wan_newsletter',
    'wan_promotions',
    'wan_travelclub',
] as const

type TPreferenceGroup = typeof PreferenceGroup[number]

interface Preferences {
    list_ids: string[]
    suppression_ids: number[]
}

export function useGetEmailToken(isLoggedIn: boolean) {
    return useQuery<{ token: string }>(
        'fetch-email-token',
        async () => {
            const response = await fetch(getEmailTokenPath)
            if (!response.ok) {
                throw new Error('Failed to fetch email token.')
            }
            return await response.json()
        },
        { retry: 3, enabled: isLoggedIn },
    )
}

export function useEmailPreferenceData(emailToken: string | undefined) {
    const { emailServiceApi } = useContext(ConfigurationContext)
    return useQuery<{ result: Record<TPreferenceGroup, Preferences> }>(
        'email-preference-data',
        async () => {
            if (!emailToken) {
                throw new Error('Unable to get email token')
            }

            const api = `${emailServiceApi}/email-preference-center/email-preferences`

            const response = await fetch(api, {
                headers: {
                    Authorization: emailToken,
                },
            })

            if (!response.ok) {
                throw new Error('Network response was not ok')
            }

            return await response.json()
        },
        { retry: 3, enabled: !!emailToken },
    )
}

export function useGetPreferences(
    preferenceResponse: Record<TPreferenceGroup, Preferences> | undefined,
    preferenceContent: EmailPreferenceSection[][],
): EmailPreferenceSection[][] {
    if (!preferenceResponse) return preferenceContent

    let contactIds: string[] = []
    let suppressionIds: number[] = []

    // Get rid of duplicated ids if there's any
    for (const group in preferenceResponse) {
        contactIds.push(
            ...preferenceResponse[group as TPreferenceGroup].list_ids,
        )
        suppressionIds.push(
            ...preferenceResponse[group as TPreferenceGroup].suppression_ids,
        )
    }
    contactIds = [...new Set(contactIds)]
    suppressionIds = [...new Set(suppressionIds)]

    // Add status to preferences
    preferenceContent.forEach((masthead) => {
        masthead.forEach((section) => {
            section.sectionItems.forEach((item) => {
                const isInContacts = item.guid && contactIds.includes(item.guid)
                const isInSuppression =
                    item.unSubId && suppressionIds.includes(item.unSubId)

                item.optIn = !!(isInContacts && !isInSuppression)
            })
        })
    })
    return preferenceContent
}

/**
 * Toggle on/off email preference request
 * Will be fulfilled in future tickets.
 */
export function useToggleEmailPreference(payload: any) {
    const { emailServiceApi } = useContext(ConfigurationContext)
    return useMutation(
        'toggle-email-preference',
        async () => {
            const api = `${emailServiceApi}/email-preference-center/toggle`
            const response = await fetch(api, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: '',
                },
                body: JSON.stringify(payload),
            })

            if (!response.ok) {
                throw new Error('Network response was not ok')
            }

            const res = await response.json()

            return res
        },
        { retry: 3 },
    )
}
